import { useMask } from "@react-input/mask";
import { FormEvent, useRef, useState } from "react";
import Phone from "../components/phone";
import {
	leadFormData,
	submitLeadForm,
} from "../utilities/lead-form-submission";
import { getUserTrackingParamsInSession } from "../utilities/trackUser";
import Checkbox from "./checkbox";
import { Button, ButtonLink } from "./client.button";
import ManOnComputerIcon from "/assets/svgs/man-on-computer.svg";

export type formStatusType = "idle" | "pending" | "success" | "error";

export type formSubmitResult = {
	formData: leadFormData;
	formStatusType: formStatusType;
};

interface Props {
	title?: string;
	onSubmit: (formData: leadFormData) => formSubmitResult;
	zipCode: string;
}

export default function ContactForm({ title, onSubmit, zipCode }: Props) {
	const [formStatus, setFormStatus] = useState<formStatusType>("idle");
	const topRef = useRef<null | HTMLDivElement>(null);
	const phoneRef = useMask({
		mask: "(___) ___-____",
		replacement: { _: /\d/ },
	});

	function onSubmitForm(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const formData = {
			...Object.fromEntries(
				new FormData(event.target as HTMLFormElement).entries(),
			),
			MarketingMeta: getUserTrackingParamsInSession(),
		} as leadFormData;

		var submitResult = onSubmit(formData);
		setFormStatus(submitResult.formStatusType);
		topRef.current?.scrollIntoView();

		if (submitResult.formStatusType === "success") {
			submitLeadForm("DesignCenterContact", formData)
				.then((response) => {
					if (!response.errors) {
						setFormStatus("success");
					} else setFormStatus("error");
				})
				.catch(() => {
					setFormStatus("error");
				});
		}
	}

	return (
		<>
			<div ref={topRef} className="bg-gray-100 px-4 py-10">
				{title ? (
					<h2 className="text-headline-3 mb-10 text-center text-primary-500">
						{title}
					</h2>
				) : null}
				<div className="flex justify-center gap-4">
					<div className="w-52 rounded-lg border bg-white p-4 text-center shadow-lg">
						<Phone showIcon={true} />
					</div>
				</div>
			</div>
			{formStatus === "success" ? (
				<div className="px-32 py-16 text-center">
					<ManOnComputerIcon />
					<p className="my-4">
						Thanks for reaching out, a product specialist will contact you
						shortly!
					</p>
					<ButtonLink href={`/?zipCode=${zipCode}`} className="block">
						Explore our Homes
					</ButtonLink>
				</div>
			) : formStatus === "error" ? (
				<div className="px-32 py-16 text-center">
					<span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-error p-4 text-[2rem] font-bold leading-[0.75] text-white">
						&#33;
					</span>
					<p className="mt-4">
						There was an error submitting your home, please try again another
						time.
					</p>
				</div>
			) : (
				<>
					<div className="p-8 text-center font-bold sm:px-16">
						or
						<br />
						Leave us your contact info and we&apos;ll reach out to you.
					</div>
					<form
						onSubmit={onSubmitForm}
						className="mx-auto grid max-w-[550px] grid-cols-2 gap-4 px-8 pb-16"
					>
						<div className="col-span-2 sm:col-span-1">
							<label className="text-xs font-semibold uppercase">
								First Name *
							</label>
							<input
								name="FirstName"
								required
								placeholder="Enter First Name"
								className="w-full border-b border-gray-400 p-2 focus-visible:border-primary-500 focus-visible:outline-none"
							/>
						</div>
						<div className="col-span-2 sm:col-span-1">
							<label className="text-xs font-semibold uppercase">
								Last Name *
							</label>
							<input
								name="LastName"
								required
								placeholder="Enter Last Name"
								className="w-full border-b border-gray-400 p-2 focus-visible:border-primary-500 focus-visible:outline-none"
							/>
						</div>
						<div className="col-span-2">
							<label className="text-xs font-semibold uppercase">
								Phone Number *
							</label>
							<input
								ref={phoneRef}
								name="PhoneNumber"
								required
								placeholder="Enter Phone Number"
								pattern="\([0-9]{3}\)\s[0-9]{3}-[0-9]{4}"
								onInput={(e) => (e.target as any).setCustomValidity("")}
								onInvalid={(e) => {
									(e.target as any).setCustomValidity(
										"Please enter a 10 digit phone number.",
									);
								}}
								className="w-full border-b border-gray-400 p-2 focus-visible:border-primary-500 focus-visible:outline-none"
							/>
						</div>
						<div className="col-span-2">
							<label className="text-xs font-semibold uppercase">Email *</label>
							<input
								name="Email"
								type="email"
								required
								placeholder="Enter Email"
								className="w-full border-b border-gray-400 p-2 focus-visible:border-primary-500 focus-visible:outline-none"
							/>
						</div>
						<div className="col-span-2">
							<label className="text-xs font-semibold uppercase">
								Comments
							</label>
							<textarea
								name="LeadComments"
								rows={6}
								placeholder="Enter Comments"
								className="w-full border-b border-gray-400 p-2 focus-visible:border-primary-500 focus-visible:outline-none"
							/>
							<div className="text-xs text-gray-500">* Required Fields</div>
						</div>
						<Checkbox
							name="WrittenTextMessageConsent"
							label="Please text me (Optional)"
							className="col-span-2"
						/>
						<p className="text-xs col-span-2">
							By clicking the checkbox and then &quot;SUBMIT,&quot; you are
							signing this disclosure, in which you authorize Clayton Homes and
							its service providers to send you marketing text messages and
							telephone calls using automated dialing technology to the mobile
							number you provided in this form. Consent not required for
							purchase. See our Terms & Conditions, which includes opt-out
							instructions.
						</p>
						<Button type="submit" className="col-span-2 my-3">
							SUBMIT
						</Button>
						<p className="text-xs col-span-2">
							By clicking &quot;SUBMIT,&quot; you agree to our{" "}
							<a
								href="https://www.claytonhomes.com/privacy/ccpa/ca-resident-financial-privacy-policy"
								target="_blank"
								className="text-primary-500 underline hover:text-primary-300 hover:no-underline"
							>
								Terms & Conditions
							</a>{" "}
							and{" "}
							<a
								href="https://www.claytonhomes.com/privacy"
								target="_blank"
								className="text-primary-500 underline hover:text-primary-300 hover:no-underline"
							>
								Privacy Policy
							</a>
							.
						</p>
					</form>
				</>
			)}
		</>
	);
}
